
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { WebSocketMessagingService } from './websocket/websocket-messaging.service';
import { ProgressTrackingService } from './websocket/progress-tracking.service';
import { WebSocketConnectionService } from './websocket/websocket-connection.service';
import { LoggingService } from './logging/logging.service';

/**
 * Main WebSocket service that provides a unified API for all WebSocket operations
 * Delegates to specialized sub-services for messaging and progress tracking
 */
@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  constructor(
    private messagingService: WebSocketMessagingService,
    private progressTrackingService: ProgressTrackingService,
    private connectionService: WebSocketConnectionService,
    private logger: LoggingService
  ) {
    // Expose WebSocketService on window for debugging and connection management
    if (typeof window !== 'undefined') {
      (window as any).ngWebSocketService = this;
    }
  }

  /**
   * Initialize the service
   */
  public async initialize(): Promise<void> {
    this.logger.info('WebSocketService', 'Initializing');
    await this.progressTrackingService.initialize();
  }

  /**
   * Get user identifier (email) from authenticated session.
   * This replaces the old userEmail/clientId logic which was unreliable (sessionStorage cleared on tab close).
   */
  public getUserIdentifier(): string {
    return this.progressTrackingService.getUserIdentifier();
  }

  /**
   * Subscribe to a WebSocket destination
   */
  public async subscribe<T>(destination: string): Promise<Observable<T>> {
    return await this.messagingService.subscribe<T>(destination);
  }

  /**
   * Send a message to a WebSocket destination
   */
  public async send(destination: string, body: any): Promise<void> {
    await this.messagingService.send(destination, body);
  }

  /**
   * Disconnect the WebSocket connection
   *
   * Always performs a clean disconnect - no "temporary" flag.
   * Follows "Design by Contract" principle: ONE way to disconnect.
   */
  public disconnect(): void {
    this.connectionService.disconnect();
  }

  /**
   * Get an observable of the WebSocket connection status
   */
  public get connected$() {
    // Pass through the connection status from the connection service
    return this.connectionService.connected$;
  }

  /**
   * Get an observable of all active operations with progress updates
   */
  public get operations$() {
    return this.progressTrackingService.operations$;
  }

  /**
   * Clean up progress tracking (called before disconnect)
   */
  public cleanupProgressTracking(): void {
    this.progressTrackingService.dispose();
  }

  /**
   * Get progress updates for a specific operation type
   */
  public getProgressForType(type: string): Observable<any> {
    return this.progressTrackingService.getProgressForType(type);
  }

  /**
   * Handle a progress update manually (used for demo/testing)
   * @param update The progress update object
   */
  public handleProgressUpdate(update: any): void {
    this.progressTrackingService.registerProgressUpdate(update);
  }
}